import { useState } from "react";
import { ArrowUpRight } from "lucide-react";
import FadeIn, { SectionHeading } from "./FadeIn";
import ArticleModal from "./ArticleModal";

const ARTICLES = [
  {
    title: "What $750K Actually Buys You",
    category: "Market Insight",
    read: "6 min read",
    excerpt: "A city-by-city look at how far a three-quarter-million budget stretches in today's Canadian market.",
    img: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=900&auto=format&fit=crop",
    intro:
      "Three-quarters of a million dollars sounds like a number that should buy certainty. In Canada, what it actually buys depends almost entirely on where you point it. Here's an honest look at what a $750,000 budget means in different markets — and how to make it work hardest.",
    sections: [
      {
        h: "Toronto & Vancouver: The Condo Question",
        p: [
          "In Canada's two most expensive metros, $750K typically means a well-located one- or two-bedroom condominium, or a townhome further from the core. Detached homes at this price point are rare and usually require compromise on condition or location.",
          "The strategy here is less about stretching the budget and more about protecting it: buying smart within reach of transit and employment hubs preserves both lifestyle and resale strength.",
        ],
      },
      {
        h: "Calgary, Edmonton & the Prairies: Real Space",
        p: [
          "The same budget opens the door to detached family homes — three or four bedrooms, a yard, a garage — in established neighbourhoods. Alberta's lack of provincial land transfer tax also means more of your money lands in the home itself rather than closing costs.",
          "For buyers relocating from larger metros, this is often where the numbers finally breathe.",
        ],
      },
      {
        h: "Montreal, Ottawa & Secondary Markets",
        p: [
          "Montreal offers duplexes and plexes — live in one unit, rent the other — a structure that can meaningfully offset your payment. Ottawa sits comfortably in townhome and smaller detached territory.",
          "In smaller centres across the country, $750K can buy premium properties outright: executive homes, acreages, even waterfront in some regions.",
        ],
      },
      {
        h: "The Real Answer",
        p: [
          "Markets move, and so do rates. The most useful question isn't 'what does $750K buy nationally' — it's 'what does my budget buy in the specific community I want to live in, with my income structure.' That's a fifteen-minute conversation with real numbers attached.",
        ],
      },
    ],
  },
  {
    title: "Fixed vs Variable Mortgages",
    category: "Strategy",
    read: "8 min read",
    excerpt: "Beyond the rate: how to choose the structure that matches your risk tolerance and timeline.",
    img: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?q=80&w=900&auto=format&fit=crop",
    intro:
      "The fixed-versus-variable debate gets framed as a bet on interest rates. It isn't. It's a decision about your cash flow, your temperament, and how long you plan to hold the mortgage. Here's how to think about it properly.",
    sections: [
      {
        h: "What You're Actually Choosing",
        p: [
          "A fixed rate locks your payment for the term — five years of identical payments, full stop. A variable rate moves with your lender's prime rate: when the Bank of Canada cuts, you pay less interest; when it hikes, more.",
          "Historically, variable rates have cost less over most long periods — but 'on average' is cold comfort if a rate spike strains your monthly budget in the meantime.",
        ],
      },
      {
        h: "The Question Nobody Asks: What's Your Exit?",
        p: [
          "Breaking a fixed mortgage can trigger an interest rate differential penalty that runs into the tens of thousands. Variable mortgages typically cap the penalty at three months' interest.",
          "If there's any chance you'll sell, move, or restructure within the term — a growing family, a job change, a separation — that penalty difference often matters more than the rate itself.",
        ],
      },
      {
        h: "Match the Product to the Person",
        p: [
          "Choose fixed when budget certainty protects your sleep, when cash flow is tight, or when you're near the top of your qualification range. Consider variable when you have income cushion, plan prepayments, or may break the mortgage early.",
          "There's also a middle path: some lenders offer hybrid structures splitting the mortgage between fixed and variable portions.",
        ],
      },
      {
        h: "What We Do Before You Decide",
        p: [
          "Before recommending either, I model your payment under rate increases, stress-test your cash flow, and map your likely five-year plans. The right answer is the one your life can comfortably carry — not the one that won last year's rate argument.",
        ],
      },
    ],
  },
  {
    title: "5 Mistakes First-Time Buyers Make",
    category: "First-Time Buyers",
    read: "5 min read",
    excerpt: "The avoidable errors that cost buyers thousands — and how a strategy session prevents them.",
    img: "https://images.unsplash.com/photo-1494526585095-c41746248156?q=80&w=900&auto=format&fit=crop",
    intro:
      "Most first-time buyer regrets aren't about the home they chose — they're about decisions made months earlier, quietly, without realizing the cost. These five are the ones I see most, and every one is preventable.",
    sections: [
      {
        h: "1. Shopping Before Getting Pre-Approved",
        p: [
          "Falling in love with a home you can't finance is the most expensive heartbreak in real estate. A proper pre-approval — documents verified, rate held — tells you your real ceiling and makes your offer credible the moment you find the one.",
        ],
      },
      {
        h: "2. Budgeting to the Maximum Approval",
        p: [
          "Lenders approve you for what you can carry on paper, not what lets you live well. Buying at your ceiling leaves nothing for furniture, repairs, or life. Decide your comfortable payment first; let the purchase price follow.",
        ],
      },
      {
        h: "3. Forgetting Closing Costs",
        p: [
          "Legal fees, title insurance, land transfer tax, inspection, appraisal, adjustments — budget 1.5% to 4% of the purchase price on top of your down payment. First-time buyer rebates help, but they don't cover everything.",
        ],
      },
      {
        h: "4. Making Credit Moves Before Closing",
        p: [
          "A new car loan, a furniture line of credit, even a new credit card between approval and closing can change your ratios and jeopardize the mortgage. Hold all borrowing until after you have the keys.",
        ],
      },
      {
        h: "5. Rushing the Inspection",
        p: [
          "In competitive markets, buyers waive inspections to win bids. Sometimes that's a calculated risk with the right guidance; often it's a five-figure surprise waiting in the foundation. Know which one you're doing before you're in the moment.",
        ],
      },
    ],
  },
  {
    title: "Mortgage Strategies for Self-Employed Professionals",
    category: "Self-Employed",
    read: "7 min read",
    excerpt: "How entrepreneurs can present income, structure write-offs and still qualify for premium financing.",
    img: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?q=80&w=900&auto=format&fit=crop",
    intro:
      "Being self-employed doesn't make you a risky borrower — it makes you a documented-differently borrower. The entrepreneurs who get premium financing aren't luckier; their files are simply built with a plan, usually years before they apply.",
    sections: [
      {
        h: "How Lenders See Your Income",
        p: [
          "Traditional lenders typically average two years of declared income from your Notices of Assessment. The tension is obvious: every write-off that saves tax also lowers the income you can borrow against.",
          "The write-offs aren't wrong — but if a purchase is on your horizon, the balance between tax efficiency and borrowing power deserves a deliberate decision, not a default.",
        ],
      },
      {
        h: "More Paths Than Your Bank Mentions",
        p: [
          "Beyond the two-year average, there are stated-income programs through alternative lenders, bank-statement-based qualification, and lenders who will consider gross business revenue or retained earnings with strong documentation.",
          "A larger down payment — 20% or more — widens the menu considerably, as does a strong credit history and low existing debt.",
        ],
      },
      {
        h: "The File That Gets Approved",
        p: [
          "Two years of clean filings, organized business financials, separation between business and personal accounts, and taxes paid on time. Presentation matters: a complete, coherent file moves to the top of an underwriter's pile.",
        ],
      },
      {
        h: "Start the Clock Early",
        p: [
          "The best self-employed approvals are built 12 to 24 months out. If homeownership is in your plans, a strategy conversation now — before this year's return is filed — can be worth tens of thousands in borrowing power.",
        ],
      },
    ],
  },
  {
    title: "How Much Should You Actually Put Down?",
    category: "Strategy",
    read: "6 min read",
    excerpt: "5%, 20% or more? The real math behind down payments, insurance premiums and opportunity cost.",
    img: "https://images.unsplash.com/photo-1554224154-26032ffc0d07?q=80&w=900&auto=format&fit=crop",
    intro:
      "'Put down 20%' is the most repeated advice in Canadian real estate — and it's right roughly as often as it's wrong. The honest answer sits in the math of insurance premiums, rate tiers, and what your cash could do elsewhere.",
    sections: [
      {
        h: "The Rules of the Game",
        p: [
          "Under $500,000, the minimum down payment is 5%. Between $500,000 and $1.5 million, it's 5% on the first portion and 10% on the rest. Above $1.5 million, or for most rental properties, 20% is required.",
          "Below 20% down, your mortgage must carry default insurance (CMHC, Sagen or Canada Guaranty) — a premium of 2.8% to 4% of the mortgage amount, usually added to the loan.",
        ],
      },
      {
        h: "The Twist: Insured Can Be Cheaper",
        p: [
          "Because insured mortgages are lower-risk for lenders, they often carry the lowest rates in the market. It's entirely possible for a 10%-down insured mortgage to price better than an uninsured one — the premium buys you a better rate.",
          "This is why the 'wait until 20%' instinct deserves a calculation, not an assumption.",
        ],
      },
      {
        h: "The Other Side of the Ledger",
        p: [
          "Every dollar in your down payment is a dollar not in your emergency fund, your RRSP, or your business. A slightly smaller down payment with six months of reserves behind it is often the stronger financial position.",
          "The right number balances three things: the insurance math, your monthly comfort, and the liquidity you keep.",
        ],
      },
      {
        h: "Run Your Actual Numbers",
        p: [
          "The difference between 5%, 10% and 20% down on your target home is a ten-minute exercise with real figures — payment, premium, rate tier, and cash remaining. That's the conversation worth having before the offer, not after.",
        ],
      },
    ],
  },
];

export default function Resources({ onLead }) {
  const [active, setActive] = useState(null);

  return (
    <section id="resources" data-testid="resources-section" className="py-24 lg:py-36 bg-charcoal-800/95 scroll-mt-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
          <SectionHeading
            label="Mortgage Intelligence"
            title="Decisions Deserve Better Information"
            sub="Concise, jargon-free briefings on the questions that actually move your bottom line."
          />
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {ARTICLES.map((a, i) => (
            <FadeIn key={a.title} delay={(i % 3) * 0.1} variant="depth" className={i === 0 ? "lg:row-span-2" : ""}>
              <article
                data-testid={`article-card-${i}`}
                role="button"
                tabIndex={0}
                aria-label={`Read article: ${a.title}`}
                onClick={() => setActive(a)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setActive(a);
                  }
                }}
                className={`group glass-panel depth-card rounded-md overflow-hidden h-full flex flex-col cursor-pointer ${
                  i === 0 ? "lg:h-full" : ""
                }`}
              >
                <div className={`relative overflow-hidden ${i === 0 ? "h-64 lg:h-80" : "h-52"}`}>
                  <img
                    src={a.img}
                    alt={a.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-charcoal/70 to-transparent" />
                  <span className="absolute top-4 left-4 px-3 py-1.5 text-[10px] tracking-[0.18em] uppercase text-champagne bg-black/50 backdrop-blur-md border border-gold/30 rounded-sm">
                    {a.category}
                  </span>
                </div>
                <div className="p-7 flex flex-col flex-1">
                  <h3 className="font-serif text-xl text-ivory leading-snug group-hover:text-champagne transition-colors duration-300">
                    {a.title}
                  </h3>
                  <p className="mt-3 text-sm text-ivory/50 font-light leading-relaxed flex-1">{a.excerpt}</p>
                  <div className="mt-6 flex items-center justify-between">
                    <span className="text-[11px] text-ivory/35 tracking-wide">{a.read}</span>
                    <span className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.2em] text-gold">
                      READ <ArrowUpRight size={13} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </span>
                  </div>
                </div>
              </article>
            </FadeIn>
          ))}
        </div>
      </div>

      <ArticleModal
        article={active}
        onClose={() => setActive(null)}
        onTalk={() => {
          setActive(null);
          onLead?.("consultation");
        }}
      />
    </section>
  );
}
